// PROV-01 · R92 · Piezas COMPARTIDAS por los dos puntos de entrada del recorrido administrativo:
//   · Cuenta App -> sus Personas            (components/admin/AccesoApp.jsx)
//   · Persona registrada -> Accesos App     (components/admin/PersonaAccesosApp.jsx)
// Una sola implementación: los modales y los endpoints de mutación son los MISMOS. Aquí no se
// resuelve autorización: el acceso efectivo lo decide el servidor en cada consulta de la App.
import { useEffect, useRef, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { notifyOk, notifyError } from "@/lib/notify";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Field } from "@/components/forms/fields";

export const ROLES_APP = [
  { v: "app_persona", label: "Persona (usa la App para sí misma)" },
  { v: "app_familiar", label: "Familiar (acompaña a otra persona)" },
];
export const PERMISOS = [
  { v: "lectura.basica", label: "Lectura básica" },
  { v: "lectura.recordatorios", label: "Recordatorios" },
  { v: "lectura.actividad", label: "Actividad" },
];
export const rolLabel = (r) => (r === "app_persona" ? "Persona" : "Familiar");

// Los modales son `display:grid`: sus hijos conservan `min-width:auto` y dos `input[type=date]`
// empujaban el track por encima del ancho útil (barra horizontal y botones cortados). Se acota
// aquí, en local, SIN tocar components/ui/* (lo comparte la pestaña FROZEN «Internos»).
export const MODAL = "w-[calc(100%-2rem)] max-w-md sm:max-w-lg rounded-2xl grid-cols-1 " +
  "max-h-[85vh] overflow-y-auto overflow-x-hidden [&>*]:min-w-0";
export const TRIGGER = "rounded-xl w-full min-w-0 [&>span]:block [&>span]:truncate [&>span]:text-left";
export const OUTLINE = "rounded-xl border-slate-300 bg-white text-[#012472] hover:bg-slate-100 " +
  "hover:text-[#012472] hover:border-slate-400 focus-visible:ring-2 focus-visible:ring-[#01CBE3] " +
  "focus-visible:ring-offset-1 disabled:opacity-60";
export const PRIMARIO = "rounded-xl bg-[#01CBE3] hover:bg-[#01B5CB] text-white";
export const AZUL = "rounded-xl bg-[#012472] hover:bg-[#01205f] text-white";

export const LIMITE = 25;

// ---------------------------------------------------------------- Lecturas paginadas (keyset)
export const buscarCuentas = (body) => api.post("/app-admin/cuentas/buscar", { limite: LIMITE, ...body });
export const buscarPersonas = (body) => api.post("/app-admin/personas-asignables/buscar", { limite: LIMITE, ...body });
export const grantsPorPersona = (body) => api.post("/app-admin/grants/por-persona", { limite: LIMITE, ...body });
export const detalleCuenta = (id) => api.get(`/app-admin/cuentas/${id}`);

export const errorDe = (e) => formatApiErrorDetail(e.response?.data?.detail) || "No se pudo completar la operación.";

// Hook mínimo de página keyset con «Cargar más». Descarta respuestas TARDÍAS de una búsqueda o
// selección anterior (token monótono): nunca se pinta el resultado de otra petición.
export function usePaginado(cargar) {
  const [items, setItems] = useState([]);
  const [cursor, setCursor] = useState(null);
  const [hayMas, setHayMas] = useState(false);
  const [cargando, setCargando] = useState(false);
  const token = useRef(0);

  const pedir = async (extra = {}, acumular = false) => {
    const mio = ++token.current;
    setCargando(true);
    try {
      const r = await cargar({ ...extra, cursor: acumular ? cursor : null });
      if (mio !== token.current) return null;
      const d = r.data || {};
      setItems((prev) => (acumular ? [...prev, ...(d.items || [])] : (d.items || [])));
      setCursor(d.next_cursor || null);
      setHayMas(!!d.has_more);
      return d;
    } catch (e) {
      if (mio === token.current) {
        setItems(acumular ? (p) => p : []);
        setCursor(null);
        setHayMas(false);
        notifyError(errorDe(e));
      }
      return null;
    } finally {
      if (mio === token.current) setCargando(false);
    }
  };

  return { items, hayMas, cargando, pedir, setItems };
}

export function CargarMas({ hayMas, cargando, onClick, testId }) {
  if (!hayMas) return null;
  return (
    <div className="pt-2">
      <Button variant="outline" size="sm" className={OUTLINE} disabled={cargando}
        data-testid={testId} onClick={onClick}>
        {cargando ? "Cargando…" : "Cargar más"}
      </Button>
    </div>
  );
}

// ---------------------------------------------------------------- Estados (H5)
// Estado de CUENTA y estado de GRANT son hechos distintos y se muestran por separado. Esta
// insignia habla SÓLO del acceso: un acceso revocado es un registro histórico de ESTE grant y no
// dice nada sobre si la cuenta puede iniciar sesión ni sobre otras autorizaciones de la Persona.
// Quien decide el acceso efectivo es el servidor en cada consulta de la App: el frontend no lo
// calcula ni lo afirma.
export function GrantEstadoBadges({ grant, testId }) {
  const estadoGrant = grant.estado === "revocado"
    ? "Acceso revocado"
    : grant.vigente ? "Acceso vigente" : "Acceso sin vigencia";
  return (
    <div className="flex flex-wrap gap-1.5" data-testid={testId} aria-label={estadoGrant}>
      <span className={`text-xs rounded-full px-2.5 py-1 ${grant.estado === "revocado"
        ? "bg-slate-100 text-slate-600"
        : grant.vigente ? "bg-emerald-50 text-emerald-700" : "bg-amber-50 text-amber-700"}`}>
        {estadoGrant}
      </span>
    </div>
  );
}

// ---------------------------------------------------------------- Confirmación (H2)
// Identifica cuenta, Persona(s) y consecuencia. Cancelar / cerrar / Escape NO mutan. El botón
// destructivo no recibe el foco por defecto y se bloquea mientras la petición está en vuelo.
export function ConfirmAccesoApp({
  open, onOpenChange, titulo, consecuencia, lineas = [], confirmar = "Confirmar",
  destructivo = false, onConfirm, testId,
}) {
  const [enviando, setEnviando] = useState(false);
  useEffect(() => { if (!open) setEnviando(false); }, [open]);
  return (
    <Dialog open={open} onOpenChange={(v) => { if (!enviando) onOpenChange(v); }}>
      <DialogContent className={MODAL} data-testid={testId}>
        <DialogHeader>
          <DialogTitle className="text-[#012472]">{titulo}</DialogTitle>
          <DialogDescription className="text-xs text-slate-500">{consecuencia}</DialogDescription>
        </DialogHeader>
        <ul className="space-y-1.5 text-sm text-slate-700">
          {lineas.filter(Boolean).map((l, i) => (
            <li key={i} data-testid={`${testId}-linea-${i}`} className="break-words">· {l}</li>
          ))}
        </ul>
        <DialogFooter className="gap-2 sticky bottom-0 bg-background pt-3 -mb-1">
          <Button variant="outline" className={OUTLINE} autoFocus disabled={enviando}
            data-testid={`${testId}-cancelar`} onClick={() => onOpenChange(false)}>
            Cancelar
          </Button>
          <Button disabled={enviando} data-testid={`${testId}-confirmar`}
            className={destructivo
              ? "rounded-xl bg-rose-700 hover:bg-rose-800 text-white focus-visible:ring-2 focus-visible:ring-rose-300"
              : AZUL}
            onClick={async () => {
              setEnviando(true);
              const ok = await onConfirm();
              setEnviando(false);
              if (ok !== false) onOpenChange(false);
            }}>
            {enviando ? "Aplicando…" : confirmar}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------- Alta de cuenta App (H1)
const cuentaVacia = { nombre: "", email: "", role: "app_familiar", password: "" };

export function CuentaAppModal({ open, onOpenChange, onCreada, emailSugerido = "" }) {
  const [f, setF] = useState(cuentaVacia);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    if (open) setF({ ...cuentaVacia, email: emailSugerido || "" });
    else setSaving(false);
  }, [open, emailSugerido]);

  const crear = async () => {
    setSaving(true);
    try {
      const r = await api.post("/app-admin/cuentas", { ...f, email: f.email.trim().toLowerCase() });
      notifyOk("Cuenta de App creada. Todavía no autoriza a ninguna persona.");
      setF(cuentaVacia);
      onOpenChange(false);
      await onCreada?.(r.data);
    } catch (e) {
      notifyError(errorDe(e));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!saving) onOpenChange(v); }}>
      <DialogContent className={MODAL} data-testid="acceso-app-modal-cuenta">
        <DialogHeader>
          <DialogTitle className="text-[#012472]">Nueva cuenta App</DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Identidad de la App Residencial. Nace SIN ninguna persona autorizada: el acceso se
            otorga después, persona por persona. No crea cuentas internas ni institucionales.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <Field label="Nombre">
            <Input className="rounded-xl" value={f.nombre} data-testid="acceso-app-nombre-input"
              onChange={(e) => setF({ ...f, nombre: e.target.value })} />
          </Field>
          <Field label="Correo">
            <Input type="email" className="rounded-xl" value={f.email} data-testid="acceso-app-email-input"
              onChange={(e) => setF({ ...f, email: e.target.value })} />
          </Field>
          <Field label="Rol">
            <Select value={f.role} onValueChange={(v) => setF({ ...f, role: v })}>
              <SelectTrigger className={TRIGGER} data-testid="acceso-app-role-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                {ROLES_APP.map((r) => <SelectItem key={r.v} value={r.v}>{r.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </Field>
          <Field label="Contraseña (mínimo 10 caracteres, letras y números)">
            <Input type="password" className="rounded-xl" value={f.password} autoComplete="new-password"
              data-testid="acceso-app-password-input"
              onChange={(e) => setF({ ...f, password: e.target.value })} />
          </Field>
        </div>
        <DialogFooter className="gap-2 sticky bottom-0 bg-background pt-3 -mb-1">
          <Button variant="outline" className={OUTLINE} disabled={saving}
            onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button disabled={saving} data-testid="acceso-app-crear-cuenta-submit" className={PRIMARIO}
            onClick={crear}>{saving ? "Creando…" : "Crear cuenta"}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------- Otorgar / editar acceso (H4)
const grantVacio = {
  tipo: "familiar", relacion: "", permisos: ["lectura.basica"],
  vigencia_desde: "", vigencia_hasta: "",
};

export function GrantModal({
  open, onOpenChange, cuenta, persona, grant, onGuardado,
}) {
  const [f, setF] = useState(grantVacio);
  const [saving, setSaving] = useState(false);
  const clave = `${grant?.grant_id || "nuevo"}·${cuenta?.user_id || ""}·${persona?.persona_id || ""}`;

  // Cambiar de cuenta/Persona/grant REINICIA el formulario: nunca se guarda una edición sobre
  // una selección distinta de la que se abrió.
  useEffect(() => {
    if (!open) { setSaving(false); return; }
    setF(grant ? {
      tipo: grant.tipo || "familiar", relacion: grant.relacion || "",
      permisos: grant.permisos || [],
      vigencia_desde: (grant.vigencia_desde || "").slice(0, 10),
      vigencia_hasta: (grant.vigencia_hasta || "").slice(0, 10),
    } : grantVacio);
  }, [open, clave, grant]);

  const toggle = (p) => setF((prev) => ({
    ...prev,
    permisos: prev.permisos.includes(p) ? prev.permisos.filter((x) => x !== p) : [...prev.permisos, p],
  }));

  const guardar = async () => {
    setSaving(true);
    const payload = {
      tipo: f.tipo, permisos: f.permisos, relacion: f.relacion,
      vigencia_desde: f.vigencia_desde || null, vigencia_hasta: f.vigencia_hasta || null,
    };
    try {
      if (grant?.grant_id) await api.put(`/app-admin/grants/${grant.grant_id}`, payload);
      else await api.post(`/app-admin/cuentas/${cuenta.user_id}/grants`, { ...payload, persona_id: persona.persona_id });
      notifyOk(grant ? "Acceso actualizado." : "Acceso otorgado.");
      onOpenChange(false);
      await onGuardado?.();
    } catch (e) {
      notifyError(errorDe(e));
    } finally { setSaving(false); }
  };

  const nombrePersona = persona?.nombre_completo || grant?.persona_nombre || "";
  const sitio = persona?.sitio_nombre || grant?.sitio_nombre || "";

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!saving) onOpenChange(v); }}>
      <DialogContent className={MODAL} data-testid="acceso-app-modal-grant">
        <DialogHeader>
          <DialogTitle className="text-[#012472]">
            {grant ? "Editar acceso" : "Otorgar acceso"}
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            El acceso es explícito por persona y se revalida en cada consulta de la App.
          </DialogDescription>
        </DialogHeader>
        {/* El contexto vive DENTRO del modal: no queda detrás del fondo oscurecido. */}
        <div className="rounded-xl bg-slate-50 border border-gray-100 p-3 space-y-1 text-sm">
          <p className="text-slate-700 break-words" data-testid="acceso-app-modal-grant-cuenta">
            <span className="text-xs uppercase tracking-wider text-slate-400 block">Cuenta App</span>
            {cuenta?.email || "—"}{cuenta?.nombre ? ` · ${cuenta.nombre}` : ""}
          </p>
          <p className="text-slate-700 break-words" data-testid="acceso-app-modal-grant-persona">
            <span className="text-xs uppercase tracking-wider text-slate-400 block">Persona protegida</span>
            {nombrePersona || "—"}{sitio ? ` · ${sitio}` : ""}
          </p>
        </div>
        <div className="space-y-4">
          <Field label="Tipo de acceso">
            <Select value={f.tipo} onValueChange={(v) => setF({ ...f, tipo: v })}>
              <SelectTrigger className={TRIGGER} data-testid="acceso-app-tipo-select"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="propio">Propio (es la misma persona)</SelectItem>
                <SelectItem value="familiar">Familiar</SelectItem>
              </SelectContent>
            </Select>
          </Field>
          <Field label="Relación familiar (opcional · no concede acceso por sí misma)">
            <Input className="rounded-xl" value={f.relacion} data-testid="acceso-app-relacion-input"
              onChange={(e) => setF({ ...f, relacion: e.target.value })} />
          </Field>
          <div className="space-y-2">
            <p className="text-sm font-medium text-slate-700">Permisos</p>
            {PERMISOS.map((p) => (
              <label key={p.v} className="flex items-center gap-2 text-sm text-slate-600">
                <input type="checkbox" checked={f.permisos.includes(p.v)}
                  data-testid={`acceso-app-permiso-${p.v}`}
                  onChange={() => toggle(p.v)} className="h-4 w-4 rounded" />
                {p.label}
              </label>
            ))}
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div className="min-w-0">
              <Field label="Vigente desde (opcional)">
                <Input type="date" className="rounded-xl w-full min-w-0" value={f.vigencia_desde}
                  data-testid="acceso-app-desde-input"
                  onChange={(e) => setF({ ...f, vigencia_desde: e.target.value })} />
              </Field>
            </div>
            <div className="min-w-0">
              <Field label="Vigente hasta (opcional)">
                <Input type="date" className="rounded-xl w-full min-w-0" value={f.vigencia_hasta}
                  data-testid="acceso-app-hasta-input"
                  onChange={(e) => setF({ ...f, vigencia_hasta: e.target.value })} />
              </Field>
            </div>
          </div>
        </div>
        <DialogFooter className="gap-2 sticky bottom-0 bg-background pt-3 -mb-1">
          <Button variant="outline" className={OUTLINE} disabled={saving}
            onClick={() => onOpenChange(false)}>Cancelar</Button>
          <Button disabled={saving || (!grant && (!cuenta?.user_id || !persona?.persona_id))}
            data-testid="acceso-app-guardar-grant" className={PRIMARIO} onClick={guardar}>
            {saving ? "Guardando…" : grant ? "Guardar cambios" : "Otorgar acceso"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ---------------------------------------------------------------- Restablecer contraseña (Vía B)
// PUT /api/users/{id} con SÓLO `password`. No es «de un solo uso» ni obliga a cambiarla: la App no
// tiene pantalla de cambio obligatorio. La contraseña no se guarda, no se registra y no se vuelve
// a mostrar: el formulario se limpia al guardar y al cancelar.
export function ResetPasswordAppModal({ open, onOpenChange, cuenta, onHecho }) {
  const [pwd, setPwd] = useState("");
  const [saving, setSaving] = useState(false);
  useEffect(() => { if (!open) { setPwd(""); setSaving(false); } }, [open]);

  const guardar = async () => {
    setSaving(true);
    try {
      await api.put(`/users/${cuenta.user_id}`, { password: pwd });
      setPwd("");
      notifyOk("Contraseña restablecida. Las sesiones anteriores de esa cuenta quedaron cerradas.");
      onOpenChange(false);
      await onHecho?.();
    } catch (e) {
      notifyError(errorDe(e));
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!saving) { if (!v) setPwd(""); onOpenChange(v); } }}>
      <DialogContent className={MODAL} data-testid="acceso-app-modal-reset">
        <DialogHeader>
          <DialogTitle className="text-[#012472]">Restablecer contraseña</DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Se aplica a esta cuenta de App. Al guardar, sus sesiones activas se cierran y deberá
            entrar de nuevo. Entrégala por el canal que la dirección ya tiene verificado.
          </DialogDescription>
        </DialogHeader>
        <div className="rounded-xl bg-slate-50 border border-gray-100 p-3 text-sm">
          <span className="text-xs uppercase tracking-wider text-slate-400 block">Cuenta App</span>
          <span className="text-slate-700 break-words" data-testid="acceso-app-reset-cuenta">
            {cuenta?.email}{cuenta?.nombre ? ` · ${cuenta.nombre}` : ""}
          </span>
        </div>
        <Field label="Contraseña nueva (mínimo 10 caracteres, letras y números)">
          <Input type="password" className="rounded-xl" value={pwd} autoComplete="new-password"
            data-testid="acceso-app-reset-password-input" onChange={(e) => setPwd(e.target.value)} />
        </Field>
        <DialogFooter className="gap-2 sticky bottom-0 bg-background pt-3 -mb-1">
          <Button variant="outline" className={OUTLINE} autoFocus disabled={saving}
            data-testid="acceso-app-reset-cancelar"
            onClick={() => { setPwd(""); onOpenChange(false); }}>Cancelar</Button>
          <Button disabled={saving || pwd.length < 10} className={AZUL}
            data-testid="acceso-app-reset-guardar" onClick={guardar}>
            {saving ? "Guardando…" : "Restablecer"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
